import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import AppHeader from '../../components/shell/AppHeader'
import StatusChip from '../../components/agent/StatusChip'
import EmptyState from '../../components/shell/EmptyState'
import { supabase } from '../../lib/supabase'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import { fullDate } from '../reports/reportDates'
import { formatTimesInText, useTimeFormat, type TimeFormat } from '../../lib/timeFormat'
import { leaveRequestDisplayState } from './leaveRequestStore'
import { getMyWorkShifts, type ShiftOnDate } from './agentSwapStore'
import {
  claimOpenShift,
  getMyClaims,
  getMyGiveaways,
  getOpenShiftsForMe,
  giveAwayShift,
  withdrawClaim,
  withdrawGiveaway,
  type ClaimResult,
  type MyClaim,
  type MyGiveaway,
  type OpenShiftForMe,
} from './agentOpenShiftStore'

function ArrowLeftIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  )
}

function hoursOf(shift: { shiftName?: string; startTime: string; endTime: string; assignmentName?: string }, format: TimeFormat): string {
  return formatTimesInText([shift.shiftName, `${shift.startTime} to ${shift.endTime}`, shift.assignmentName].filter(Boolean).join(', '), format)
}

const STATE_LABEL = { open: 'Offered', filled: 'Taken', cancelled: 'Withdrawn' } as const

/**
 * docs/product/scheduling-model.md's "Open shifts", from the agent's side:
 * the slots the supervisor has posted for the agent's team, a claim on each,
 * and the agent's own claims with their decisions.
 */
export default function AgentOpenShiftsPage() {
  const identity = useProfileIdentity()
  const { format } = useTimeFormat()
  const [userId, setUserId] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [shifts, setShifts] = useState<OpenShiftForMe[]>([])
  const [claims, setClaims] = useState<MyClaim[]>([])
  const [giveaways, setGiveaways] = useState<MyGiveaway[]>([])
  const [myShifts, setMyShifts] = useState<ShiftOnDate[]>([])
  const [result, setResult] = useState<ClaimResult | null>(null)
  const [giveDate, setGiveDate] = useState('')
  const [giveMessage, setGiveMessage] = useState('')

  const load = useCallback(async (id: string) => {
    const [loadedShifts, loadedClaims, loadedGiveaways, loadedMine] = await Promise.all([
      getOpenShiftsForMe(id),
      getMyClaims(id),
      getMyGiveaways(id),
      getMyWorkShifts(id),
    ])
    setShifts(loadedShifts)
    setClaims(loadedClaims)
    setGiveaways(loadedGiveaways)
    setMyShifts(loadedMine)
    setLoaded(true)
  }, [])

  useEffect(() => {
    let cancelled = false
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const id = session?.user.id
      if (!id || cancelled) return
      setUserId(id)
      await load(id)
    })
    return () => {
      cancelled = true
    }
  }, [load])

  async function claim(openShiftId: string) {
    if (!userId) return
    const made = await claimOpenShift(userId, openShiftId)
    setResult(made)
    if (made.ok) await load(userId)
  }

  async function withdraw(id: string) {
    if (!userId) return
    const withdrawn = await withdrawClaim(id)
    setResult(withdrawn)
    if (withdrawn.ok) await load(userId)
  }

  async function giveAway(event: FormEvent) {
    event.preventDefault()
    if (!userId) return
    if (!giveDate) {
      setResult({ ok: false, message: 'Choose the shift to give away.' })
      return
    }
    const offered = await giveAwayShift(userId, giveDate, giveMessage)
    setResult(offered)
    if (offered.ok) {
      setGiveDate('')
      setGiveMessage('')
      await load(userId)
    }
  }

  async function withdrawOffer(id: string) {
    if (!userId) return
    const withdrawn = await withdrawGiveaway(userId, id)
    setResult(withdrawn)
    if (withdrawn.ok) await load(userId)
  }

  // A shift already on offer is not offered twice.
  const offeredDates = new Set(giveaways.filter((offer) => offer.state === 'open').map((offer) => offer.date))
  const givable = myShifts.filter((shift) => !offeredDates.has(shift.date))
  const claimFor = (openShiftId: string) => claims.find((item) => item.openShiftId === openShiftId && item.state === 'pending')

  return (
    <div className="mx-auto flex h-dvh w-full max-w-md flex-col border-line bg-canvas text-ink sm:border-x lg:max-w-none lg:border-x-0">
      <a
        href="#open-shifts-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="open-shifts-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto">
        <div className="mx-auto flex w-full max-w-2xl flex-1 flex-col gap-6 p-4 lg:p-8">
          <Link to="/agent" className="flex min-h-11 items-center gap-1.5 self-start rounded-control text-small font-bold text-ink-muted">
            <ArrowLeftIcon />
            Dashboard
          </Link>

          <div>
            <h1 className="text-h3 font-extrabold lg:text-h2">Open Shifts</h1>
            <p className="mt-0.5 text-small text-ink-muted">Shifts your supervisor has posted for your team. Claim one and your supervisor decides.</p>
          </div>

          {result && !result.ok && (
            <p role="alert" className="text-caption font-semibold text-error">
              {result.message}
            </p>
          )}

          <section aria-labelledby="available-heading" className="flex flex-col gap-3">
            <h2 id="available-heading" className="text-h4 font-extrabold">
              Available
            </h2>
            {!loaded ? (
              <p role="status" className="text-small text-ink-muted">
                Loading&hellip;
              </p>
            ) : shifts.length === 0 ? (
              <EmptyState>No open shifts right now.</EmptyState>
            ) : (
              <ul className="flex flex-col gap-2">
                {shifts.map((shift) => {
                  const mine = claimFor(shift.id)
                  return (
                    <li key={shift.id} className="rounded-surface border border-line p-3">
                      <p className="text-small font-bold">{fullDate(shift.date)}</p>
                      <p className="text-small">{hoursOf(shift, format)}</p>
                      {shift.releasedByName && <p className="text-caption text-ink-muted">Given away by {shift.releasedByName}</p>}
                      {shift.note && <p className="text-caption text-ink-muted">{shift.note}</p>}
                      {mine ? (
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <p className="text-small font-semibold text-ink-muted">Claim submitted</p>
                          <button type="button" onClick={() => withdraw(mine.id)} className="flex min-h-11 items-center px-2 text-small font-bold text-primary">
                            Withdraw
                            <span className="sr-only"> your claim for {fullDate(shift.date)}</span>
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => claim(shift.id)}
                          className="mt-2 flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
                        >
                          Claim
                          <span className="sr-only"> the shift on {fullDate(shift.date)}</span>
                        </button>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          <section aria-labelledby="claims-heading" className="flex flex-col gap-3">
            <h2 id="claims-heading" className="text-h4 font-extrabold">
              Your claims
            </h2>
            {claims.length === 0 ? (
              <p className="text-small text-ink-muted">You have not claimed a shift.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {claims.map((item) => (
                  <li key={item.id} className="rounded-surface border border-line p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-small font-bold">{fullDate(item.date)}</p>
                        <p className="text-small">{hoursOf(item, format)}</p>
                      </div>
                      <StatusChip state={leaveRequestDisplayState(item.state)} />
                    </div>
                    {item.state === 'denied' && item.decisionComment && (
                      <p className="mt-2 text-small">
                        <span className="font-bold">Denied: </span>
                        {item.decisionComment}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="give-heading" className="flex flex-col gap-3">
            <div>
              <h2 id="give-heading" className="text-h4 font-extrabold">
                Give away a shift
              </h2>
              <p className="mt-0.5 text-small text-ink-muted">Offer one of your shifts to the team. You keep it until a teammate claims it and your supervisor approves.</p>
            </div>
            <form onSubmit={giveAway} aria-label="Give away a shift" className="flex flex-col gap-3 rounded-surface border border-line bg-surface-2 p-3.5">
              <div>
                <label htmlFor="give-shift" className="mb-1 block text-caption font-bold text-ink-muted">
                  Your shift
                </label>
                <select
                  id="give-shift"
                  value={giveDate}
                  onChange={(event) => setGiveDate(event.target.value)}
                  className="min-h-11 w-full rounded-control border border-line-strong bg-canvas px-2.5 py-2 text-small"
                >
                  <option value="">Choose one of your shifts</option>
                  {givable.map((shift) => (
                    <option key={shift.date} value={shift.date}>
                      {fullDate(shift.date)}, {formatTimesInText(shift.label, format)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="give-message" className="mb-1 block text-caption font-bold text-ink-muted">
                  Message (optional)
                </label>
                <input
                  id="give-message"
                  type="text"
                  maxLength={500}
                  value={giveMessage}
                  onChange={(event) => setGiveMessage(event.target.value)}
                  className="min-h-11 w-full rounded-control border border-line-strong bg-canvas px-2.5 py-2 text-small"
                />
              </div>
              <button
                type="submit"
                className="flex min-h-11 items-center justify-center self-start rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
              >
                Give away shift
              </button>
            </form>

            {giveaways.length > 0 && (
              <ul className="flex flex-col gap-2">
                {giveaways.map((offer) => (
                  <li key={offer.id} className="rounded-surface border border-line p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-small font-bold">{fullDate(offer.date)}</p>
                        <p className="text-small">{hoursOf(offer, format)}</p>
                        {offer.note && <p className="text-caption text-ink-muted">{offer.note}</p>}
                      </div>
                      <span className="rounded-full bg-surface-2 px-2.5 py-0.5 text-caption font-bold whitespace-nowrap">{STATE_LABEL[offer.state]}</span>
                    </div>
                    {offer.state === 'open' && (
                      <button type="button" onClick={() => withdrawOffer(offer.id)} className="mt-1 flex min-h-11 items-center text-small font-bold text-primary">
                        Withdraw
                        <span className="sr-only"> your offer of {fullDate(offer.date)}</span>
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>
    </div>
  )
}
